import {
  AccessLevel,
  Currency,
  Difficulty,
  FillInStatus,
  PaymentStatus,
  Placement,
  Status,
  SubscriptionStatus
} from '@prisma/client';
import { isString } from '../types/guards';
import { BadgeInterface } from '../types/shared';
import { InvalidDataError } from '../../errors/shared';

export const setChipDifficulty = (difficulty: Difficulty): BadgeInterface => {
  switch (difficulty) {
    case Difficulty.EASY:
      return {
        color: 'jade',
        label: getLabelByDifficulty(Difficulty.EASY)
      };
    case Difficulty.MEDIUM:
      return {
        color: 'sky',
        label: getLabelByDifficulty(Difficulty.MEDIUM)
      };
    case Difficulty.HARD:
      return {
        color: 'orange',
        label: getLabelByDifficulty(Difficulty.HARD)
      };
    default:
      return {
        color: 'gray',
        label: 'Не установлен'
      };
  }
};

export const labelToDifficulty = new Map([
  ['Базовый уровень', Difficulty.EASY],
  ['Средний уровень', Difficulty.MEDIUM],
  ['Продвинутый уровень', Difficulty.HARD]
]);

export const difficultyToLabel = new Map(
  [...labelToDifficulty].map(([label, diff]) => [diff, label])
);

// Функции для получения данных
export const getDifficultyByLabel = (label: string) => {
  const difficulty = labelToDifficulty.get(label);
  if (!difficulty) throw new Error('Неподдерживаемое значение сложности');
  return difficulty;
};

export const getLabelByDifficulty = (difficulty: Difficulty) => {
  return difficultyToLabel.get(difficulty);
};

export const queryToDifficulty = new Map([
  ['basic', Difficulty.EASY],
  ['intermediate', Difficulty.MEDIUM],
  ['advanced', Difficulty.HARD]
]);

export const difficultyToQuery = new Map(
  [...queryToDifficulty].map(([param, diff]) => [diff, param])
);

// Функции для получения данных
export const getDifficultyByQuery = (param: string) => {
  const difficulty = queryToDifficulty.get(param);
  if (!difficulty) throw new Error('Неподдерживаемое значение сложности');
  return difficulty;
};

export const getQueryByDifficulty = (difficulty: Difficulty) => {
  return difficultyToQuery.get(difficulty);
};

export const difficultyLabelToQuery = new Map();

for (const [label, difficulty] of labelToDifficulty) {
  for (const [query, queryDifficulty] of queryToDifficulty) {
    if (difficulty === queryDifficulty) {
      difficultyLabelToQuery.set(label, query);
    }
  }
}

export const queryToDifficultyLabel = new Map(
  [...difficultyLabelToQuery].map(([label, query]) => [query, label])
);

export const getQueryByDifficultyLabel = (label: string) => {
  return difficultyLabelToQuery.get(label);
};
export const getDifficultyLabelByQuery = (query: string) => {
  return queryToDifficultyLabel.get(query);
};

const labelToPlacement = new Map([
  ['laterally', Placement.LATERALLY],
  ['below', Placement.BELOW]
]);

const placementToLabel = new Map(
  [...labelToPlacement].map(([label, placement]) => [placement, label])
);

// Функции для получения данных
export const getPlacementByLabel = (label: string): Placement => {
  const placement = labelToPlacement.get(label);
  if (!placement) throw new Error('Неподдерживаемое значение размещения');
  return placement;
};

export const getLabelByPlacement = (placement: Placement): string => {
  const label = placementToLabel.get(placement);
  if (!label) throw new Error('Неподдерживаемое значение размещения');
  return label;
};

export const labelToSize = new Map([
  ['300 px', 300],
  ['600 px', 600]
]);

export const sizeToLabel = new Map(
  [...labelToSize].map(([label, size]) => [size, label])
);

// Функции для получения данных
export const getSizeByLabel = (label: string): number => {
  const size = labelToSize.get(label);
  if (!size) throw new Error('Неподдерживаемое значение ширины/высоты');
  return size;
};

export const getLabelBySize = (size: number): string => {
  const label = sizeToLabel.get(size);
  if (!label) throw new Error('Неподдерживаемое значение ширины/высоты');
  return label;
};

export const transformLabelToSizeValue = (value: unknown): number => {
  if (isString(value) && getSizeByLabel(value)) return getSizeByLabel(value);
  throw new Error('Неверный тип значения');
};

export const getBirthYear = (birthday: string): number | null => {
  if (!birthday) return null;

  const parts = birthday.split('.');

  if (parts.length !== 3) throw new Error('Неверный формат даты');

  return Number(parts[2]);
};

export const labelToStatus = new Map([
  ['В работе', Status.PENDING],
  ['На проверке', Status.REVIEW],
  ['Отклонена', Status.REJECTED],
  ['Опубликована', Status.PUBLISHED]
]);

export const statusToLabel = new Map(
  [...labelToStatus].map(([label, status]) => [status, label])
);

// Функции для получения данных
export const getStatusByLabel = (label: string) => {
  const status = labelToStatus.get(label);
  if (!status) throw new Error('Неподдерживаемое значение статуса');
  return status;
};

export const getLabelByStatus = (status: Status) => {
  return statusToLabel.get(status);
};

export const queryToStatus = new Map([
  ['pending', Status.PENDING],
  ['review', Status.REVIEW],
  ['rejected', Status.REJECTED],
  ['published', Status.PUBLISHED]
]);

export const statusToQuery = new Map(
  [...queryToStatus].map(([query, status]) => [status, query])
);

export const getStatusByQuery = (query: string): Status => {
  const status = queryToStatus.get(query);
  if (!status) throw new Error('Неподдерживаемое значение статуса');
  return status;
};

export const statusLabelToQuery = new Map();

for (const [label, status] of labelToStatus) {
  for (const [query, queryStatus] of queryToStatus) {
    if (status === queryStatus) {
      statusLabelToQuery.set(label, query);
    }
  }
}

export const getQueryByStatusLabel = (label: string) => {
  return statusLabelToQuery.get(label);
};

// Статус наполнения
export const labelToFillStatus = new Map([
  ['В ожидании', FillInStatus.EMPTY],
  ['В процессе', FillInStatus.FILLING],
  ['Завершён', FillInStatus.COMPLETED]
]);

export const fillInStatusToLabel = new Map(
  [...labelToFillStatus].map(([label, fillStatus]) => [fillStatus, label])
);

// Функции для получения данных
export const getFillInStatusByLabel = (label: string): FillInStatus => {
  const status = labelToFillStatus.get(label);
  if (!status) throw new Error('Неподдерживаемое значение статуса наполнения');
  return status;
};

export const getLabelByFillInStatus = (fillStatus: FillInStatus) => {
  return fillInStatusToLabel.get(fillStatus);
};

export const transformLabelToFillInStatus = (value: unknown): FillInStatus => {
  if (isString(value) && getFillInStatusByLabel(value))
    return getFillInStatusByLabel(value);
  throw new Error('Неверный тип значения');
};

export const transformLabelToStatus = (value: unknown): Status => {
  if (isString(value) && getStatusByLabel(value))
    return getStatusByLabel(value);
  throw new Error('Неверный тип значения');
};

export const transformLabelToDifficulty = (value: unknown): Difficulty => {
  if (isString(value) && getDifficultyByLabel(value))
    return getDifficultyByLabel(value);
  throw new Error('Неверный тип значения');
};

export const transformLabelToPlacement = (value: unknown): Placement => {
  if (isString(value) && getPlacementByLabel(value))
    return getPlacementByLabel(value);
  throw new Error('Неверный тип значения');
};

export const accessLevelToLаbel = new Map([
  [AccessLevel.NONE, 'Нет доступа'],
  [AccessLevel.RESTRICTED, 'Ограниченный'],
  [AccessLevel.FULL, 'Полный']
]);

export const getLabelByAccessLevel = (accessLevel: AccessLevel) => {
  return accessLevelToLаbel.get(accessLevel);
};

export const subscriptionStatusToString = new Map([
  [SubscriptionStatus.PENDING, 'pending'],
  [SubscriptionStatus.SUBSCRIBED, 'subscribed'],
  [SubscriptionStatus.UNSUBSCRIBED, 'unsubscribed']
]);

export const getStringBySubscriptionStatus = (
  subscriptionStatus: SubscriptionStatus
) => {
  return subscriptionStatusToString.get(subscriptionStatus);
};

export const stringToSubscriptionStatus = new Map(
  [...subscriptionStatusToString].map(([subscriptionStatus, stringValue]) => [
    stringValue,
    subscriptionStatus
  ])
);

export const getSubscriptionStatusByString = (stringValue: string) => {
  return stringToSubscriptionStatus.get(stringValue);
};

// Валюта
export const stringToCurrency = new Map([
  ['RUB', Currency.RUB],
  ['EUR', Currency.EUR],
  ['USD', Currency.USD]
]);

export const getCurrencyByString = (currencyString: string): Currency => {
  const currency = stringToCurrency.get(currencyString);

  if (!currency) throw new InvalidDataError('Неподдерживаемое значение валюты');

  return currency;
};

// Статус платежа
export const stringToPaymentStatus = new Map([
  ['succeeded', PaymentStatus.SUCCEEDED],
  ['canceled', PaymentStatus.CANCELED],
  ['pending', PaymentStatus.PENDING]
]);

export const getPaymentStatusByString = (
  statusString: string
): PaymentStatus => {
  const paymentStatus = stringToPaymentStatus.get(statusString);

  if (!paymentStatus)
    throw new InvalidDataError('Неподдерживаемое значение статуса платежа');

  return paymentStatus;
};
